import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export { AuthContext };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sios_token');
    const savedUser = localStorage.getItem('sios_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      authAPI.me()
        .then((res) => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('sios_token');
          localStorage.removeItem('sios_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // Empty deps is intentional - only run on mount

  const login = useCallback(async (email, password, otpCode = null) => {
    const res = await authAPI.login({ email, password, otp_code: otpCode });
    
    // If 2FA required, return the response without setting user
    if (res.data.requires_2fa) {
      return res.data;
    }
    
    const { access_token, user: userData } = res.data;
    localStorage.setItem('sios_token', access_token);
    localStorage.setItem('sios_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await authAPI.register({ name, email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('sios_token', access_token);
    localStorage.setItem('sios_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('sios_token');
    localStorage.removeItem('sios_user');
    setUser(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ 
    user, 
    loading, 
    login, 
    register, 
    logout 
  }), [user, loading, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
